"use client";

import Link from "next/link";
import Image from "next/image";
import { useRef, useState } from "react";
import { getBlogImageUrl, formatBlogDate } from "@/lib/blog-utils";

export interface BlogPostPreview {
  id: number;
  title: string;
  slug: string;
  excerpt: string | null;
  cover_image: string | null;
  author_name: string | null;
  published_at: string;
  categories: { name: string; slug: string }[];
}

export function LatestArticlesSection({ posts }: { posts: BlogPostPreview[] }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [mobileIndex, setMobileIndex] = useState(0);

  if (posts.length === 0) return null;

  const scrollToPost = (index: number) => {
    const container = scrollRef.current;
    if (!container) return;
    const cards = container.querySelectorAll<HTMLElement>("[data-article-card]");
    const card = cards[index];
    if (!card) return;
    container.scrollTo({ left: card.offsetLeft - container.offsetLeft, behavior: "smooth" });
    setMobileIndex(index);
  };

  const handleScroll = () => {
    const container = scrollRef.current;
    if (!container || window.matchMedia("(min-width: 768px)").matches) return;
    const cards = Array.from(container.querySelectorAll<HTMLElement>("[data-article-card]"));
    if (cards.length === 0) return;
    const closestIndex = cards.reduce((bestIndex, card, index) => {
      const best = cards[bestIndex];
      return Math.abs(card.offsetLeft - container.scrollLeft) < Math.abs(best.offsetLeft - container.scrollLeft)
        ? index
        : bestIndex;
    }, 0);
    setMobileIndex(closestIndex);
  };

  return (
    <section className="py-12 bg-white">
      <div className="container mx-auto px-4">
        <h2 className="text-2xl font-bold mb-8 flex items-center gap-3">
          <Image src="/logo-vignette.png" alt="" width={32} height={32} className="h-8 w-8" />
          Les dernières actualités Toolouer
        </h2>

        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-2 md:pb-0 md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-6 md:overflow-visible"
        >
          {posts.map((post) => (
            <Link
              key={post.id}
              href={`/blog/${post.slug}`}
              data-article-card
              className="group bg-white rounded-2xl border overflow-hidden hover:shadow-lg transition-shadow flex flex-col flex-none w-[84vw] max-w-[340px] snap-start md:w-auto md:max-w-none md:min-w-0"
            >
              {/* Cover image */}
              <div className="relative aspect-[16/10] bg-gray-100">
                {post.cover_image ? (
                  <Image
                    src={getBlogImageUrl(post.cover_image)}
                    alt={post.title}
                    fill
                    className="object-cover"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-300">
                    <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z" />
                    </svg>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-5 flex flex-col flex-1">
                {/* Categories */}
                {post.categories.length > 0 && (
                  <div className="hidden flex-wrap gap-2 mb-2 md:flex">
                    {post.categories.map((cat) => (
                      <span key={cat.slug} className="text-xs font-medium text-secondary bg-secondary/10 px-2 py-0.5 rounded-full">
                        {cat.name}
                      </span>
                    ))}
                  </div>
                )}

                <h3 className="font-bold text-lg mb-2 group-hover:text-secondary transition-colors line-clamp-2">
                  {post.title}
                </h3>

                {post.excerpt && (
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2 flex-1">
                    {post.excerpt}
                  </p>
                )}

                <div className="hidden items-center justify-between text-xs text-gray-400 mt-auto pt-3 border-t border-gray-100 md:flex">
                  <span>Par {post.author_name || "Toolouer"}</span>
                  <span>{formatBlogDate(post.published_at)}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {posts.length > 1 && (
          <div className="mt-4 flex justify-center gap-2 md:hidden" aria-label="Pagination des actualités">
            {posts.map((post, index) => (
              <button
                key={post.id}
                type="button"
                onClick={() => scrollToPost(index)}
                className={`h-3 w-3 rounded-full transition-colors ${index === mobileIndex ? "bg-primary" : "bg-secondary"}`}
                aria-label={`Afficher l'actualité ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
